/**
 * InstitutionenHierarchiePage – Verwaltung der Institutionen-Hierarchie
 */
import { useState, useEffect, useCallback } from 'react';
import { Building2, Search, GitMerge, RefreshCw, Loader2, TreePine, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TreeView } from '@/components/institutionen/TreeNode';
import { InstitutionDetail } from '@/components/institutionen/InstitutionDetail';
import { MergePanel } from '@/components/institutionen/MergePanel';
import { StewardPanel } from '@/components/institutionen/StewardPanel';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function authHeaders() {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('olympus_token')}` };
}

export default function InstitutionenHierarchiePage() {
  const [tree, setTree] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState('hierarchie');

  const loadTree = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/institutionen/tree`, { headers: authHeaders() });
      setTree(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadDuplicates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/institutionen/migration/duplicates`, { headers: authHeaders() });
      setDuplicates(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadTree(), loadDuplicates()]);
    setLoading(false);
  }, [loadTree, loadDuplicates]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/institutionen/search?q=${encodeURIComponent(searchTerm)}`, { headers: authHeaders() });
        setSearchResults(await res.json());
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="institutionen-hierarchie-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Building2 className="w-6 h-6" /> Institutionen-Hierarchie
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{tree.length} Institutionen | {duplicates.length} Dubletten</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadAll} disabled={loading} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="hierarchie" className="flex items-center gap-1.5">
            <TreePine className="w-4 h-4" /> Hierarchie
          </TabsTrigger>
          <TabsTrigger value="dubletten" className="flex items-center gap-1.5">
            <GitMerge className="w-4 h-4" /> Dubletten
            {duplicates.length > 0 && <Badge className="bg-amber-500 text-white text-[10px] ml-1">{duplicates.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="steward" className="flex items-center gap-1.5">
            <Bot className="w-4 h-4" /> KI-Steward
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hierarchie">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 border rounded-lg bg-white overflow-hidden">
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Institution suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-9 text-sm" data-testid="institution-search" />
                </div>
              </div>
              <div className="max-h-[600px] overflow-y-auto p-1">
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : searchTerm.length >= 2 ? (
                  searchResults.length === 0 ? (
                    <p className="text-center text-xs text-slate-400 py-4">Keine Treffer</p>
                  ) : searchResults.map(inst => (
                    <div key={inst.id} onClick={() => setSelected(inst)}
                      className={`flex items-center gap-2 py-1.5 px-3 rounded cursor-pointer text-sm ${selected?.id === inst.id ? 'bg-blue-100 text-blue-800' : 'hover:bg-slate-100 text-slate-700'}`}>
                      <Building2 className="w-3.5 h-3.5 text-slate-500" />
                      <span className="truncate">{inst.name}</span>
                      {inst.parent_name && <span className="text-[10px] text-slate-400 ml-auto">{inst.parent_name}</span>}
                    </div>
                  ))
                ) : (
                  <TreeView nodes={tree} onSelect={setSelected} selectedId={selected?.id} />
                )}
              </div>
            </div>
            <div className="lg:col-span-3 border rounded-lg bg-white overflow-hidden">
              <InstitutionDetail institution={selected} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dubletten">
          <Card>
            <MergePanel duplicates={duplicates} onMergeComplete={loadAll} />
          </Card>
        </TabsContent>

        <TabsContent value="steward">
          <Card>
            <StewardPanel />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
